import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '~/lib/api'

// The pipeline is per team (prospects scouted for it plus unassigned ones), not per season: a
// recruiting pipeline runs across season ends, so the key carries the team only.
export const scoutingKeys = {
  all: ['scouting'] as const,
  prospects: (teamId: string, archived: boolean) => ['scouting', teamId, 'prospects', archived] as const,
  prospect: (id: string) => ['scouting', 'prospect', id] as const,
  search: (q: string) => ['scouting', 'search', q] as const,
}

export const prospectsQuery = (teamId: string, archived = false) =>
  queryOptions({
    queryKey: scoutingKeys.prospects(teamId, archived),
    queryFn: () =>
      unwrap(api.scouting.prospects.$get({ query: { ...(teamId ? { teamId } : {}), ...(archived ? { archived: 'true' as const } : {}) } })),
  })
export type ProspectList = Awaited<ReturnType<NonNullable<ReturnType<typeof prospectsQuery>['queryFn']>>>
export type ProspectRow = ProspectList['items'][number]

export const prospectQuery = (id: string) =>
  queryOptions({
    queryKey: scoutingKeys.prospect(id),
    queryFn: () => unwrap(api.scouting.prospects[':id'].$get({ param: { id } })),
  })
export type Prospect = Awaited<ReturnType<NonNullable<ReturnType<typeof prospectQuery>['queryFn']>>>
export type Report = Prospect['reports'][number]
export type Trial = Prospect['trials'][number]

export const searchQuery = (q: string) =>
  queryOptions({
    queryKey: scoutingKeys.search(q),
    queryFn: ({ signal }) => unwrap(api.scouting.search.$get({ query: { q } }, { init: { signal } })),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  })

function useRefresh(alsoSquad = false) {
  const qc = useQueryClient()
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: scoutingKeys.all }),
      ...(alsoSquad ? [qc.invalidateQueries({ queryKey: ['people'] })] : []),
    ])
}

type CreateBody = Parameters<typeof api.scouting.prospects.$post>[0]['json']
type PatchBody = Parameters<(typeof api.scouting.prospects)[':id']['$patch']>[0]['json']
type ReportBody = Parameters<(typeof api.scouting.prospects)[':id']['reports']['$post']>[0]['json']
type TrialBody = Parameters<(typeof api.scouting.prospects)[':id']['trials']['$post']>[0]['json']
type SignBody = Parameters<(typeof api.scouting.prospects)[':id']['sign']['$post']>[0]['json']
type FromUrlBody = Parameters<(typeof api.scouting.prospects)['from-url']['$post']>[0]['json']
export type StatusValue = Parameters<(typeof api.scouting.prospects)[':id']['status']['$post']>[0]['json']['status']

export function useCreateProspect() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (json: CreateBody) => unwrap(api.scouting.prospects.$post({ json })), onSuccess: refresh })
}

export function useFromSquadEntry() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: { squadEntryId: string; teamId?: string | null }) => unwrap(api.scouting.prospects['from-squad-entry'].$post({ json })),
    onSuccess: refresh,
  })
}

export function useFromPerson() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: { refPersonId: string; teamId?: string | null }) => unwrap(api.scouting.prospects['from-person'].$post({ json })),
    onSuccess: refresh,
  })
}

export function usePreviewUrl() {
  return useMutation({ mutationFn: (url: string) => unwrap(api.scouting.prospects['preview-url'].$post({ json: { url } })) })
}

export function useFromUrl() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (json: FromUrlBody) => unwrap(api.scouting.prospects['from-url'].$post({ json })), onSuccess: refresh })
}

export function useUpdateProspect(id: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: PatchBody) => unwrap(api.scouting.prospects[':id'].$patch({ param: { id }, json })),
    onSuccess: refresh,
  })
}

/** Status moves are optimistic on the board: the card jumps column at once. */
export function useMoveProspect(teamId: string) {
  const qc = useQueryClient()
  const key = scoutingKeys.prospects(teamId, false)
  return useMutation({
    mutationFn: (v: { id: string; status: StatusValue; note?: string | null }) =>
      unwrap(api.scouting.prospects[':id'].status.$post({ param: { id: v.id }, json: { status: v.status, note: v.note ?? null } })),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<ProspectList>(key)
      if (prev) qc.setQueryData<ProspectList>(key, { ...prev, items: prev.items.map((p) => (p.id === v.id ? { ...p, status: v.status } : p)) })
      return { prev }
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(key, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: scoutingKeys.all }),
  })
}

export function useArchiveProspect(id: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (archived: boolean) => unwrap(api.scouting.prospects[':id'].archive.$post({ param: { id }, json: { archived } })),
    onSuccess: refresh,
  })
}

export function useDeleteProspect() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.scouting.prospects[':id'].$delete({ param: { id } })),
    // Not the deleted profile itself: it would refetch (404) while its page is still mounted.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: scoutingKeys.all, predicate: (q) => q.queryKey[1] !== 'prospect' }),
        qc.invalidateQueries({ queryKey: ['people'] }),
      ]),
  })
}

export function useSignProspect(id: string) {
  const refresh = useRefresh(true)
  return useMutation({
    mutationFn: (json: SignBody) => unwrap(api.scouting.prospects[':id'].sign.$post({ param: { id }, json })),
    onSuccess: refresh,
  })
}

export function useSaveReport(prospectId: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id?: string; json: ReportBody }) =>
      v.id
        ? unwrap(api.scouting.reports[':id'].$patch({ param: { id: v.id }, json: v.json }))
        : unwrap(api.scouting.prospects[':id'].reports.$post({ param: { id: prospectId }, json: v.json })),
    onSuccess: refresh,
  })
}

export function useDeleteReport() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (id: string) => unwrap(api.scouting.reports[':id'].$delete({ param: { id } })), onSuccess: refresh })
}

export function useSaveTrial(prospectId: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id?: string; json: Partial<TrialBody> }) =>
      v.id
        ? unwrap(api.scouting.trials[':id'].$patch({ param: { id: v.id }, json: v.json }))
        : unwrap(api.scouting.prospects[':id'].trials.$post({ param: { id: prospectId }, json: v.json as TrialBody })),
    onSuccess: refresh,
  })
}

export function useDeleteTrial() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (id: string) => unwrap(api.scouting.trials[':id'].$delete({ param: { id } })), onSuccess: refresh })
}
